import React, { useState } from 'react';

function NotesPanel({ notes, currentUserId, onAdd, onEdit, onDelete, isReadOnly, compact = false }) {
  const [newNote, setNewNote] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);

  const handleAdd = () => {
    if (newNote.trim()) {
      onAdd({ content: newNote.trim() });
      setNewNote('');
    }
  };

  const handleSave = () => {
    if (editingNote && editingNote.content.trim()) {
      onEdit(editingNote.id, { content: editingNote.content.trim() });
      setEditingNote(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ca-ES', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper to get owner ID (handles both object and primitive formats)
  const getOwnerId = (note) => {
    if (note.createdBy && typeof note.createdBy === 'object') {
      return note.createdBy.id;
    }
    return note.createdBy;
  };

  // Helper to get display name
  const getDisplayName = (note) => {
    if (note.createdBy && typeof note.createdBy === 'object') {
      if (note.createdBy.displayName) return note.createdBy.displayName;
      if (note.createdBy.firstName && note.createdBy.lastName) {
        return `${note.createdBy.firstName} ${note.createdBy.lastName}`;
      }
      return note.createdBy.email?.split('@')[0] || 'Usuari';
    }
    return note.userName || 'Usuari';
  };

  // Helper to get initials
  const getInitials = (note) => {
    if (note.createdBy && typeof note.createdBy === 'object') {
      if (note.createdBy.displayName) return note.createdBy.displayName.charAt(0).toUpperCase();
      if (note.createdBy.firstName) {
        return note.createdBy.firstName.charAt(0).toUpperCase();
      }
      return note.createdBy.email?.charAt(0).toUpperCase() || 'U';
    }
    return note.userName?.charAt(0).toUpperCase() || 'U';
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header - only show if not compact */}
      {!compact && (
        <div className="p-4 border-b border-border bg-surface">
          <h3 className="font-semibold text-content flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Notes personals ({notes.length})
          </h3>
        </div>
      )}

      {/* Add note */}
      {!isReadOnly && (
        <div className={`${compact ? 'p-3' : 'p-4'} border-b border-border bg-surface-subtle`}>
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Escriu una nova nota..."
            rows={compact ? 2 : 3}
            className="w-full px-3 py-2 text-sm border border-border-strong rounded-lg focus:ring-1 focus:ring-amber-500 focus:border-amber-500 resize-none bg-surface text-content placeholder-content-disabled"
          />
          <div className="flex justify-end mt-2">
            <button
              onClick={handleAdd}
              disabled={!newNote.trim()}
              className="px-4 py-1.5 text-sm bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Afegir
            </button>
          </div>
        </div>
      )}

      {/* Notes List */}
      <div className={`flex-1 overflow-y-auto ${compact ? 'p-3 space-y-2' : 'p-4 space-y-3'}`}>
        {notes.length === 0 ? (
          <div className="text-center py-8">
            <svg className="w-12 h-12 mx-auto text-content-disabled mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <p className="text-content-muted text-sm">No hi ha notes</p>
            {!isReadOnly && (
              <p className="text-content-disabled text-xs mt-1">Les teves notes són privades i només tu les pots veure</p>
            )}
          </div>
        ) : (
          notes.map((note) => {
            const isOwner = getOwnerId(note) === currentUserId;
            const isEditing = editingNote?.id === note.id;

            return (
              <div
                key={note.id}
                className={`bg-surface rounded-lg border ${isOwner ? 'border-warning/30' : 'border-border'} shadow-sm`}
              >
                <div className="p-3">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${isOwner ? 'bg-warning-subtle text-warning-text' : 'bg-surface-muted text-content-secondary'}`}>
                        {getInitials(note)}
                      </div>
                      <div className="ml-2">
                        <span className="text-sm font-medium text-content">
                          {getDisplayName(note)}
                          {isOwner && <span className="text-xs text-warning ml-1">(tu)</span>}
                        </span>
                        <span className="text-xs text-content-muted ml-2">
                          {formatDate(note.createdAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  {isEditing ? (
                    <div>
                      <textarea
                        value={editingNote.content}
                        onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-border-strong rounded-lg focus:ring-1 focus:ring-primary-500 focus:border-primary-500 resize-none bg-surface text-content"
                        autoFocus
                      />
                      <div className="flex justify-end mt-2 space-x-2">
                        <button
                          onClick={() => setEditingNote(null)}
                          className="px-3 py-1 text-sm text-content-secondary hover:text-content"
                        >
                          Cancel·lar
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={!editingNote.content.trim()}
                          className="px-3 py-1 text-sm bg-amber-500 text-white rounded hover:bg-amber-600 disabled:opacity-50"
                        >
                          Guardar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-content-secondary whitespace-pre-wrap">
                      {note.content}
                    </div>
                  )}

                  {/* Actions */}
                  {!isReadOnly && isOwner && !isEditing && (
                    <div className="mt-2 pt-2 border-t border-border-subtle flex justify-end space-x-2">
                      <button
                        onClick={() => setEditingNote({ id: note.id, content: note.content })}
                        className="text-xs text-content-muted hover:text-content-secondary"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => setShowDeleteModal(note)}
                        className="text-xs text-error hover:text-error-text"
                      >
                        Eliminar
                      </button>
                    </div>
                  )}

                  {/* Updated indicator */}
                  {note.updatedAt && note.updatedAt !== note.createdAt && (
                    <p className="text-xs text-content-disabled mt-2">
                      Editat: {formatDate(note.updatedAt)}
                    </p>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-border">
              <h3 className="text-lg font-semibold text-content">Eliminar Nota</h3>
            </div>
            <div className="p-4">
              <p className="text-content-secondary">
                Estàs segur que vols eliminar aquesta nota?
              </p>
              <div className="mt-3 p-3 bg-surface-subtle rounded text-sm text-content-secondary line-clamp-3">
                {showDeleteModal.content}
              </div>
            </div>
            <div className="p-4 border-t border-border flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="px-4 py-2 text-sm text-content-secondary border border-border-strong rounded hover:bg-surface-subtle"
              >
                Cancel·lar
              </button>
              <button
                onClick={() => {
                  onDelete(showDeleteModal.id);
                  setShowDeleteModal(null);
                }}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotesPanel;
